import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Plus, Trash2, FileText } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { usePermissions } from '../contexts/permissions'
import { apiError, fmtDate, type IndustryTenant, type MaterialRow } from '../lib/industria'
import {
  RECIPE_STATUS_LABEL, fmtQtyPerUnit,
  type RecipeDetail, type RecipeRow, type RecipeStatus,
} from '../lib/producao'
import type { Product } from '@wsfull/shared'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

const STATUS_BADGE: Record<RecipeStatus, 'default' | 'success' | 'info'> = {
  draft: 'default', active: 'success', obsolete: 'info',
}

type ItemForm = {
  materialId: string; qtyPerUnit: string; scrapPct: string; scrapLimitPct: string; required: boolean
}

const emptyItem: ItemForm = { materialId: '', qtyPerUnit: '1', scrapPct: '0', scrapLimitPct: '0', required: true }

export function IndustriaFichasPage() {
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-fichas', 'edit')

  const [tenantId, setTenantId] = useState('')
  const [openId, setOpenId] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [form, setForm] = useState({ tenantId: '', productId: '', validFrom: '', notes: '' })
  const [items, setItems] = useState<ItemForm[]>([{ ...emptyItem }])

  const { data: tenants = [] } = useQuery<IndustryTenant[]>({
    queryKey: ['industria', 'tenants'],
    queryFn: async () => (await api.get('/materials/tenants')).data,
  })

  const { data: recipes = [], isLoading } = useQuery<RecipeRow[]>({
    queryKey: ['producao', 'recipes', tenantId],
    queryFn: async () => (await api.get('/product-recipes', {
      params: { tenantId: tenantId || undefined },
    })).data,
  })

  const { data: detail } = useQuery<RecipeDetail>({
    queryKey: ['producao', 'recipe', openId],
    queryFn: async () => (await api.get(`/product-recipes/${openId}`)).data,
    enabled: !!openId,
  })

  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ['products', form.tenantId],
    queryFn: async () => (await api.get('/products', { params: { tenantId: form.tenantId } })).data,
    enabled: formOpen && !!form.tenantId,
  })

  const { data: materials = [] } = useQuery<MaterialRow[]>({
    queryKey: ['industria', 'materials', 'all'],
    queryFn: async () => (await api.get('/materials')).data,
    enabled: formOpen,
  })

  const createMutation = useMutation({
    mutationFn: async () => (await api.post('/product-recipes', {
      tenantId: form.tenantId,
      productId: form.productId,
      validFrom: form.validFrom || null,
      notes: form.notes.trim() || null,
      items: items.filter(i => i.materialId).map(i => ({
        materialId: i.materialId,
        qtyPerUnit: Number(i.qtyPerUnit),
        scrapPct: Number(i.scrapPct || 0),
        scrapLimitPct: Number(i.scrapLimitPct || 0),
        required: i.required,
      })),
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['producao', 'recipes'] })
      setFormOpen(false)
      setItems([{ ...emptyItem }])
    },
    onError: (e) => setError(apiError(e, 'Não foi possível criar a ficha')),
  })

  const activateMutation = useMutation({
    mutationFn: async (id: string) => (await api.post(`/product-recipes/${id}/activate`, {})).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['producao', 'recipes'] })
      queryClient.invalidateQueries({ queryKey: ['producao', 'recipe'] })
    },
    onError: (e) => setError(apiError(e, 'Não foi possível ativar')),
  })

  const usableItems = items.filter(i => i.materialId).length

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Fichas técnicas</h2>
          <p className="text-sm text-gray-500">
            O que entra em uma unidade. Versionada: trocou o rótulo, nasce versão nova — e a OP
            de três meses atrás continua contando a verdade
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => { setError(null); setFormOpen(true) }}>
            <Plus className="h-4 w-4" /> Nova ficha
          </Button>
        )}
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="w-64">
            <label className="mb-1 block text-sm font-medium text-gray-700">Marca</label>
            <select className={selectClass} value={tenantId} onChange={e => setTenantId(e.target.value)}>
              <option value="">Todas</option>
              {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : recipes.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <FileText className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">
                Nenhuma ficha — sem ela a ordem de produção não sabe o que consumir
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Produto</th>
                    <th className="px-4 py-2.5 text-left font-medium">Marca</th>
                    <th className="px-4 py-2.5 text-right font-medium">Versão</th>
                    <th className="px-4 py-2.5 text-right font-medium">Itens</th>
                    <th className="px-4 py-2.5 text-left font-medium">Vale a partir de</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {recipes.map(r => (
                    <tr
                      key={r.id}
                      className={`cursor-pointer hover:bg-blue-50/40 ${openId === r.id ? 'bg-blue-50/60' : ''}`}
                      onClick={() => setOpenId(openId === r.id ? null : r.id)}
                    >
                      <td className="px-4 py-2.5">
                        <span className="font-medium text-gray-900">{r.productName}</span>
                        <span className="block font-mono text-xs text-gray-400">{r.productSku}</span>
                      </td>
                      <td className="px-4 py-2.5 text-gray-600">{r.tenantName}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">v{r.version}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">{r.itemCount}</td>
                      <td className="px-4 py-2.5 text-gray-600">{fmtDate(r.validFrom)}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant={STATUS_BADGE[r.status]}>{RECIPE_STATUS_LABEL[r.status]}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {canEdit && r.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={activateMutation.isPending}
                            onClick={e => { e.stopPropagation(); setError(null); activateMutation.mutate(r.id) }}
                          >
                            <CheckCircle2 className="h-4 w-4" /> Ativar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {openId && detail && (
        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">
              {detail.productName} · v{detail.version}
            </span>
            <span className="text-xs text-gray-500">{detail.items.length} componente(s) por unidade</span>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Material</th>
                    <th className="px-4 py-2.5 text-right font-medium">Por unidade</th>
                    <th className="px-4 py-2.5 text-right font-medium">Perda esperada</th>
                    <th className="px-4 py-2.5 text-right font-medium">Teto sem aprovação</th>
                    <th className="px-4 py-2.5 text-left font-medium">Obrigatório</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {detail.items.map(it => (
                    <tr key={it.id}>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{it.materialName}</span>
                        <span className="block font-mono text-xs text-gray-400">{it.materialCode}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {fmtQtyPerUnit(it.qtyPerUnit)} <span className="text-xs text-gray-400">{it.unit}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">{Number(it.scrapPct)}%</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">{Number(it.scrapLimitPct)}%</td>
                      <td className="px-4 py-2.5">
                        {it.required ? <Badge variant="info">sim</Badge> : <Badge>opcional</Badge>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <Modal title="Nova ficha técnica" open={formOpen} onClose={() => setFormOpen(false)} size="lg">
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); createMutation.mutate() }}
        >
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Marca</label>
              <select
                className={selectClass}
                value={form.tenantId}
                onChange={e => setForm({ ...form, tenantId: e.target.value, productId: '' })}
                required
              >
                <option value="">Selecione...</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Produto</label>
              <select
                className={selectClass}
                value={form.productId}
                onChange={e => setForm({ ...form, productId: e.target.value })}
                disabled={!form.tenantId}
                required
              >
                <option value="">Selecione...</option>
                {products.filter(p => p.active).map(p => (
                  <option key={p.id} value={p.id}>{p.sku} — {p.name}</option>
                ))}
              </select>
            </div>
            <Input
              label="Vale a partir de"
              type="date"
              value={form.validFrom}
              onChange={e => setForm({ ...form, validFrom: e.target.value })}
            />
            <Input
              label="Observações"
              value={form.notes}
              onChange={e => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Componentes por unidade</span>
              <Button type="button" size="sm" variant="ghost" onClick={() => setItems([...items, { ...emptyItem }])}>
                <Plus className="h-4 w-4" /> Item
              </Button>
            </div>
            <p className="mb-2 text-xs text-gray-500">
              Fração é esperada: a caixa master de 24 entra como 0,041667 por unidade.
            </p>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 items-center gap-2">
                  <select
                    className={`${selectClass} col-span-5`}
                    value={it.materialId}
                    onChange={e => setItems(items.map((x, j) => j === i ? { ...x, materialId: e.target.value } : x))}
                  >
                    <option value="">Material...</option>
                    {materials.filter(m => m.active).map(m => (
                      <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
                    ))}
                  </select>
                  <input
                    className="col-span-2 h-10 rounded-lg border border-gray-300 px-2 text-sm"
                    type="number"
                    step="0.000001"
                    min={0}
                    placeholder="qtd"
                    value={it.qtyPerUnit}
                    onChange={e => setItems(items.map((x, j) => j === i ? { ...x, qtyPerUnit: e.target.value } : x))}
                  />
                  <input
                    className="col-span-2 h-10 rounded-lg border border-gray-300 px-2 text-sm"
                    type="number"
                    step="0.1"
                    min={0}
                    placeholder="perda %"
                    value={it.scrapPct}
                    onChange={e => setItems(items.map((x, j) => j === i ? { ...x, scrapPct: e.target.value } : x))}
                  />
                  <input
                    className="col-span-2 h-10 rounded-lg border border-gray-300 px-2 text-sm"
                    type="number"
                    step="0.1"
                    min={0}
                    placeholder="teto %"
                    value={it.scrapLimitPct}
                    onChange={e => setItems(items.map((x, j) => j === i ? { ...x, scrapLimitPct: e.target.value } : x))}
                  />
                  <button
                    type="button"
                    className="col-span-1 text-gray-300 hover:text-red-500"
                    onClick={() => setItems(items.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-gray-500">
              Nasce como rascunho — ativar é um segundo passo.
            </span>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>Cancelar</Button>
              <Button
                type="submit"
                loading={createMutation.isPending}
                disabled={!form.productId || usableItems === 0}
              >
                Criar rascunho
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  )
}
